/**
 * Making a governed change, and saying what the platform did about it.
 *
 * Authorization, Brains and Memory each grew their own copy of this — the same
 * `Outcome`, the same correlation-id handling, the same result line, character
 * for character. That is not three implementations of one idea; it is one
 * implementation written three times, and the copies had already begun to
 * answer the same question differently.
 *
 * It lives beside the other primitives rather than inside `primitives.tsx`
 * because it is not a shape: it runs a request, decides what a refusal means
 * and mints the id the evidence is found by. Putting that in the file that
 * holds `Pill` and `Fact` would make that file two things at once.
 *
 * **The existing behaviour is the specification.** Nothing here is a redesign,
 * and one real difference is preserved rather than smoothed away: Brains tells
 * two kinds of `403` apart, because on that screen they mean different things.
 * It is passed in at the call site instead of being merged into a single
 * translator that would have to serve both and would serve neither.
 */

import { NexusError } from "../api/errors";
import { CorrelationLink, Mono, Pill } from "./primitives";

/** What happened, and where the evidence is. */
export interface Outcome {
  tone: "ok" | "bad";
  message: string;
  correlationId: string;
}

/** How a refusal is put into words. Screens with a second kind supply their own. */
export type Refusal = (error: unknown, whatWasRefused: string) => string;

/**
 * A refusal that arrived after the control had already been offered.
 *
 * `NXP-BASE-003` made revocation take effect immediately, so this is a state
 * the platform really produces: the projection said yes, the grant went away,
 * and the route said no. Reporting it as a failure would send somebody to look
 * for a broken platform.
 */
export function refusalOf(error: unknown, whatWasRefused: string): string {
  if (error instanceof NexusError) {
    if (error.isForbidden) {
      return `Refused. ${whatWasRefused} is not permitted for you — this was offered on a decision that has since changed.`;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "The change did not happen.";
}

/**
 * What each reason code the decision point refuses with means, in words.
 *
 * The platform's own vocabulary (`ReasonCode` in
 * `src/nexus/core/contracts/authorization.py`, plus the API boundary's
 * `caller_kind_refused`). Only refusing codes are listed: an allowing reason
 * never arrives on a `403`.
 */
const REFUSAL_REASONS: Record<string, string> = {
  classification_forbidden:
    "this data classification requires an access policy that is not active",
  role_not_granted: "no role you hold grants this operation",
  deny_by_default: "nothing grants this operation",
  resource_out_of_scope: "this resource is outside the scope of what you were granted",
  purpose_not_permitted: "your grant does not cover the purpose this operation is performed for",
  step_up_required: "this operation needs a multifactor sign-in",
  explicit_deny: "an access policy explicitly denies it",
  permission_kind_mismatch: "you hold this operation, but not as the kind of permission it needs",
  caller_kind_refused: "this kind of caller may never perform it",
};

/** The reason codes a refusal carried, if the platform said. */
export function reasonCodesOf(error: unknown): string[] {
  if (!(error instanceof NexusError) || !error.isForbidden) return [];
  const codes = error.detail.reason_codes;
  return Array.isArray(codes) ? codes.filter((code): code is string => typeof code === "string") : [];
}

/**
 * A refusal, with the reason the platform gave for it.
 *
 * Where the decision point named why, that is what the operator is told —
 * `Nexus refused: this data classification requires an access policy that is
 * not active` sends somebody to a data owner, where "not permitted" sends them
 * to an administrator who has already granted the role. Without a reason it is
 * exactly `refusalOf`, so nothing is invented.
 */
export function reasonedRefusal(error: unknown, whatWasRefused: string): string {
  const codes = reasonCodesOf(error);
  if (codes.length === 0) return refusalOf(error, whatWasRefused);
  const said = codes.map((code) => REFUSAL_REASONS[code] ?? code);
  return `Nexus refused: ${said.join("; ")}. ${whatWasRefused} did not happen (${codes.join(", ")}).`;
}

/**
 * A read the platform refused, with its reason — where `Show` would say only
 * "you do not have access".
 *
 * For reads a screen offers because the capability report said yes, and which
 * the route can still refuse on a sharper question (a person's linkage is PII;
 * the projection asks only at the floor). Saying the reason is what separates
 * "ask for a role" from "ask a data owner to activate a policy".
 */
export function RefusedRead({ error, what }: { error: unknown; what: string }) {
  const codes = reasonCodesOf(error);
  const said = codes.map((code) => REFUSAL_REASONS[code] ?? code);
  const correlation = error instanceof NexusError ? error.correlationId : null;
  return (
    <div className="nx-state" role="status">
      <p className="nx-state__title">
        {said.length > 0 ? `Nexus refused: ${said.join("; ")}.` : `You do not have access to ${what}.`}
      </p>
      <p>Nexus refused to show {what}. Nothing was read.</p>
      {codes.length > 0 ? <p className="nx-mono">{codes.join(", ")}</p> : null}
      {correlation ? (
        <p className="nx-hint">
          Correlation <CorrelationLink value={correlation} />
        </p>
      ) : null}
    </div>
  );
}

/**
 * Run one governed change, and keep the correlation id it was made under.
 *
 * The id is minted here rather than inside the client because the operator has
 * to be able to *see* it: a change with no way back to its audit event leaves
 * an evidence trail nobody can enter. It is traceability only — these endpoints
 * do not implement `Idempotency-Key`, so nothing here is safe to repeat and
 * nothing here pretends to be.
 */
export async function change(
  run: (correlationId: string) => Promise<unknown>,
  whatWasRefused: string,
  succeeded: string,
  refuse: Refusal = refusalOf,
): Promise<Outcome> {
  const correlationId = crypto.randomUUID();
  try {
    await run(correlationId);
    return { tone: "ok", message: succeeded, correlationId };
  } catch (error: unknown) {
    return {
      tone: "bad",
      message: refuse(error, whatWasRefused),
      // A refusal the platform described carries its own id; one that never
      // reached the platform is still findable by the id we sent.
      correlationId:
        error instanceof NexusError && error.correlationId !== null
          ? error.correlationId
          : correlationId,
    };
  }
}

/**
 * Where the confirmation of a change lives.
 *
 * At the page, deliberately, and not in the panel that performed it. A change
 * refreshes the collection it belongs to, the collection is briefly not `ready`,
 * and whatever was open — the version panel, the binding form — unmounts while
 * that happens. Holding the result there would destroy the operator's
 * confirmation and its link to the evidence at exactly the moment they were
 * produced.
 *
 * Holding it here also means no screen state has to survive a reload, which is
 * the reason there is nothing to scope to a tenant or an organization: there is
 * no retained answer to leak, only this caller's own account of what they just
 * did, and the session unmounts the page before it can become somebody else's.
 */
export function Result({ outcome }: { outcome: Outcome | null }) {
  if (outcome === null) return null;
  return (
    <p role="status" style={{ marginTop: 10 }}>
      <Pill tone={outcome.tone === "ok" ? "ok" : "bad"}>
        {outcome.tone === "ok" ? "done" : "refused"}
      </Pill>{" "}
      {outcome.message}
      <span className="nx-hint" style={{ display: "block", marginTop: 4 }}>
        Correlation <CorrelationLink value={outcome.correlationId} />
      </span>
    </p>
  );
}

/**
 * The authority an operation needs, where the operation is not offered.
 *
 * Deliberately not a disabled control and deliberately not a claim about who
 * else holds it — only what this operation requires and whether this caller has
 * it. Read from the capability report, so it stops being true the moment the
 * answer changes.
 */
export function RequiredAuthority({ capability, what }: { capability: string; what: string }) {
  return (
    <div className="nx-hint" style={{ marginTop: 10 }}>
      <div>
        <strong>{what}</strong>
      </div>
      <div>
        Requires <Mono>{capability}</Mono>
      </div>
      <div>Your authority: not granted</div>
    </div>
  );
}
